import React, { useCallback, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import "./Lens.css";

/**
 * Hover magnifier: base image + masked scaled copy following the cursor.
 * @param {object} props
 * @param {React.ReactNode} props.children — typically a single <img />
 * @param {number} [props.zoomFactor=1.3]
 * @param {number} [props.lensSize=170]
 * @param {{ x: number, y: number }} [props.position]
 * @param {{ x: number, y: number }} [props.defaultPosition]
 * @param {boolean} [props.isStatic]
 * @param {number} [props.duration=0.1]
 * @param {string} [props.ariaLabel]
 */
export function Lens({
  children,
  zoomFactor = 1.3,
  lensSize = 170,
  isStatic = false,
  position = { x: 0, y: 0 },
  defaultPosition,
  duration = 0.1,
  ariaLabel = "Zoom area",
}) {
  if (zoomFactor < 1) {
    throw new Error("zoomFactor must be greater than 1");
  }
  if (lensSize < 0) {
    throw new Error("lensSize must be greater than 0");
  }

  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState(position);

  const setPositionFromEvent = useCallback((e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePosition({
      x: Math.max(0, Math.min(rect.width, x)),
      y: Math.max(0, Math.min(rect.height, y)),
    });
  }, []);

  const currentPosition = useMemo(() => {
    if (isStatic) return position;
    if (defaultPosition && !isHovering) return defaultPosition;
    return mousePosition;
  }, [isStatic, position, defaultPosition, isHovering, mousePosition]);

  const handleMouseMove = useCallback(
    (e) => {
      setPositionFromEvent(e);
    },
    [setPositionFromEvent]
  );

  const handleMouseEnter = useCallback(
    (e) => {
      setIsHovering(true);
      setPositionFromEvent(e);
    },
    [setPositionFromEvent]
  );

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") setIsHovering(false);
  }, []);

  /* Hard circular mask — no soft fade at the rim (مش عايز يختفي من الأطراف). */
  const r = lensSize / 2;
  const maskImage = useMemo(
    () =>
      `radial-gradient(circle ${r}px at ${currentPosition.x}px ${currentPosition.y}px, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 99.85%, rgba(255,255,255,0) 100%)`,
    [currentPosition.x, currentPosition.y, r]
  );

  const maskCommon = useMemo(
    () => ({
      maskImage,
      WebkitMaskImage: maskImage,
      maskRepeat: "no-repeat",
      WebkitMaskRepeat: "no-repeat",
      maskSize: "100% 100%",
      WebkitMaskSize: "100% 100%",
      maskPosition: "0 0",
      WebkitMaskPosition: "0 0",
    }),
    [maskImage]
  );

  const { x, y } = currentPosition;

  const lensContent = (
    <motion.div
      key="lens-content"
      initial={{ opacity: 0, scale: 0.25 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.2 }}
      transition={{
        opacity: { duration: 0.18, ease: "easeOut" },
        scale:   { duration: 0.28, ease: [0.22, 1.2, 0.36, 1] },
      }}
      className="lens-lens-layer"
      style={{
        ...maskCommon,
        transformOrigin: `${x}px ${y}px`,
        zIndex: 50,
      }}
    >
      <div
        className="lens-scale-wrap"
        style={{
          transform: `scale(${zoomFactor})`,
          transformOrigin: `${x}px ${y}px`,
        }}
      >
        {children}
      </div>
    </motion.div>
  );

  return (
    <div
      className="lens"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovering(false)}
      onMouseMove={handleMouseMove}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      {children}
      {isStatic || defaultPosition ? (
        lensContent
      ) : (
        <AnimatePresence mode="popLayout">{isHovering ? lensContent : null}</AnimatePresence>
      )}
    </div>
  );
}
